<script lang="ts">
    import { onMount } from 'svelte';
    import { api } from '../../framework/js/api.js';
    import { auth } from '../../framework/js/auth.js';
    import { toast } from '../../framework/js/toast.js';
    import CreateTeamModal from './CreateTeamModal.svelte';
    import ConfirmDialog from '../ConfirmDialog.svelte';
    import {
        AppPageHeader,
        AppLoadingState,
        AppEmptyState,
        StatusBadge,
    } from '../../framework/components/app';
    import UserAvatar from '../../framework/components/UserAvatar.svelte';

    export let invoiceUuid: string | null = null;

    let team: any = null;
    let members: any[] = [];
    let loading = true;
    let inviteEmail = '';
    let inviting = false;
    let teamName = '';
    let editingName = false;
    let savingName = false;
    let showCreateTeamModal = false;

    let confirmDialog: any = {
        show: false, title: '', message: '', type: 'warning', confirmText: 'Confirm', onConfirm: () => {}
    };

    $: teamUuid = $auth?.user?.active_team_uuid;

    let loaded = false;

    $: if (teamUuid && !loaded) {
        loaded = true;
        loadTeamData();
    }

    onMount(() => {
        if (!teamUuid) loading = false;
    });

    async function loadTeamData() {
        loading = true;
        const [teamRes, membersRes]: any[] = await Promise.all([
            api.getTeam(teamUuid),
            api.getTeamMembers(teamUuid),
        ]);
        if (teamRes.success) {
            team = teamRes.data.team;
            teamName = team.name;
        }
        if (membersRes.success) members = membersRes.data.members;
        loading = false;
    }

    async function handleInvite() {
        if (!inviteEmail.trim()) return;
        inviting = true;
        const res: any = await api.inviteTeamMember(teamUuid, inviteEmail.trim());
        toast.showNotification(res.success, res.message);
        if (res.success) {
            inviteEmail = '';
            await loadTeamData();
        }
        inviting = false;
    }

    function handleRemoveMember(userUuid: string, memberName: string) {
        confirmDialog = {
            show: true,
            title: 'Remove team member',
            message: `Are you sure you want to remove ${memberName} from the team? They will lose access to all team resources.`,
            type: 'danger',
            confirmText: 'Remove',
            onConfirm: async () => {
                const res: any = await api.removeTeamMember(teamUuid, userUuid);
                toast.showNotification(res.success, res.message);
                if (res.success) await loadTeamData();
            },
        };
    }

    function handleCancelInvite(invitationUuid: string, email: string) {
        confirmDialog = {
            show: true,
            title: 'Cancel invitation',
            message: `Are you sure you want to cancel the invitation for ${email}?`,
            type: 'warning',
            confirmText: 'Cancel invite',
            onConfirm: async () => {
                const res: any = await api.cancelTeamInvitation(teamUuid, invitationUuid);
                toast.showNotification(res.success, res.message);
                if (res.success) await loadTeamData();
            },
        };
    }

    function handleRoleChange(userUuid: string, currentRole: string, memberName: string) {
        const newRole = currentRole === 'owner' ? 'member' : 'owner';
        const isPromoting = newRole === 'owner';
        confirmDialog = {
            show: true,
            title: isPromoting ? 'Promote to owner' : 'Demote to member',
            message: isPromoting
                ? `Promote ${memberName} to owner? They will be able to manage team members, billing, and settings.`
                : `Demote ${memberName} to member? They will lose the ability to manage team members, billing, and settings.`,
            type: isPromoting ? 'info' : 'warning',
            confirmText: isPromoting ? 'Promote' : 'Demote',
            onConfirm: async () => {
                const res: any = await api.updateTeamMemberRole(teamUuid, userUuid, newRole);
                toast.showNotification(res.success, res.message);
                if (res.success) await loadTeamData();
            },
        };
    }

    async function handleSaveName() {
        if (!teamName.trim() || teamName === team.name) {
            editingName = false;
            return;
        }
        savingName = true;
        const res: any = await api.updateTeam(teamUuid, { name: teamName.trim() });
        toast.showNotification(res.success, res.message);
        if (res.success) {
            team = res.data.team;
            teamName = team.name;
            window.dispatchEvent(new CustomEvent('team-updated'));
        }
        savingName = false;
        editingName = false;
    }

    function isCurrentUser(userUuid: string) {
        return $auth?.user?.uuid === userUuid;
    }

    function isOwner() {
        const currentUserMember = members.find((m) => m.user_uuid === $auth?.user?.uuid);
        return currentUserMember?.role === 'owner';
    }

    function canLeaveTeam() {
        if (!members.length) return false;
        const me = members.find((m) => m.user_uuid === $auth?.user?.uuid);
        if (!me || me.status !== 'active') return false;
        if (me.role === 'owner') {
            const ownerCount = members.filter((m) => m.role === 'owner' && m.status === 'active').length;
            if (ownerCount <= 1) return false;
        }
        return true;
    }

    function handleLeaveTeam() {
        confirmDialog = {
            show: true,
            title: 'Leave team',
            message: `Are you sure you want to leave ${team.name}? You will lose access to all team briefs and resources.`,
            type: 'danger',
            confirmText: 'Leave team',
            onConfirm: async () => {
                const res: any = await api.leaveTeam(teamUuid);
                toast.showNotification(res.success, res.message);
                if (res.success) {
                    auth.update((state: any) => {
                        if (state.user) state.user.active_team_uuid = res.data?.new_active_team_uuid || null;
                        return state;
                    });
                    window.dispatchEvent(new CustomEvent('team-updated'));
                    loaded = false;
                    team = null;
                }
            },
        };
    }

    function handleTeamCreated() {
        loaded = false;
        team = null;
        window.dispatchEvent(new CustomEvent('team-updated'));
    }

    function displayName(m: any) {
        if (!m?.user) return 'Unknown';
        const combined = `${m.user.first_name || ''} ${m.user.last_name || ''}`.trim();
        return combined || m.user.email;
    }
</script>

<div class="team-page" data-invoice-context={invoiceUuid || undefined}>
    <AppPageHeader
        title="My Team"
        subtitle="Manage your team members."
        actionLabel="New team"
        actionIcon="plus"
        showAction={false}
    >
        <button slot="actions" type="button" class="primary-btn" on:click={() => (showCreateTeamModal = true)}>
            <i class="plus icon" aria-hidden="true"></i>
            <span>New team</span>
        </button>
    </AppPageHeader>

    <CreateTeamModal bind:showModal={showCreateTeamModal} on:created={handleTeamCreated} />

    {#if loading}
        <AppLoadingState label="Loading team" />
    {:else if !team}
        <AppEmptyState
            icon="users"
            title="No team found"
            description="Create a team to get started, or contact support if this is unexpected."
        />
    {:else}
        <section class="section">
                <div class="team-card">
                    <div class="team-head">
                        {#if editingName && isOwner()}
                            <div class="name-edit">
                                <input
                                    type="text"
                                    bind:value={teamName}
                                    on:keydown={(e) => e.key === 'Enter' && handleSaveName()}
                                    class="name-input"
                                    aria-label="Team name"
                                />
                                <button type="button" class="primary-btn small" on:click={handleSaveName} disabled={savingName}>
                                    {savingName ? 'Saving…' : 'Save'}
                                </button>
                                <button type="button" class="secondary-btn small" on:click={() => { editingName = false; teamName = team.name; }}>
                                    Cancel
                                </button>
                            </div>
                        {:else}
                            <span class="team-name">{team.name}</span>
                            {#if isOwner()}
                                <button
                                    type="button"
                                    class="icon-btn"
                                    on:click={() => (editingName = true)}
                                    aria-label="Edit team name"
                                    title="Edit team name"
                                >
                                    <i class="pencil icon" aria-hidden="true"></i>
                                </button>
                            {/if}
                        {/if}
                    </div>
                    <div class="team-meta">
                        <span class="meta-item">
                            <i class="users icon" aria-hidden="true"></i>
                            <span>{members.filter((m) => m.status === 'active').length}{team.max_seats ? `/${team.max_seats}` : ''} seats</span>
                        </span>
                        <span class="meta-item">
                            <i class="credit card icon" aria-hidden="true"></i>
                            <span>
                                {#if team.payment_plan}
                                    {team.payment_plan.name}
                                {:else if team.is_on_trial}
                                    Free trial
                                {:else}
                                    No plan
                                {/if}
                            </span>
                        </span>
                        {#if team.is_on_trial}
                            <StatusBadge label={`${team.trial_days_remaining} days remaining`} tone="warn" />
                        {/if}
                    </div>

                    {#if canLeaveTeam()}
                        <div class="leave-row">
                            <button type="button" class="danger-btn small" on:click={handleLeaveTeam}>Leave team</button>
                        </div>
                    {/if}
                </div>
            </section>

            <section class="section">
                <h2 class="section-title">Members</h2>
                <ul class="member-list">
                    {#each members.filter((m) => m.status === 'active') as member (member.user_uuid)}
                        <li class="member-row">
                            <div class="member-info">
                                <UserAvatar
                                    firstName={member.user?.first_name || ''}
                                    lastName={member.user?.last_name || ''}
                                    email={member.user?.email || ''}
                                    imageUrl={member.user?.profile_image_url || ''}
                                    size={40}
                                />
                                <div class="member-detail">
                                    <div class="member-name">
                                        {displayName(member)}
                                        {#if isCurrentUser(member.user_uuid)}
                                            <StatusBadge label="You" tone="info" />
                                        {/if}
                                    </div>
                                    <div class="member-email">{member.user?.email || ''}</div>
                                </div>
                            </div>
                            <div class="member-actions">
                                <StatusBadge
                                    label={member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                                    tone={member.role === 'owner' ? 'info' : 'neutral'}
                                />
                                {#if isOwner() && !isCurrentUser(member.user_uuid)}
                                    <button
                                        type="button"
                                        class="secondary-btn small"
                                        on:click={() => handleRoleChange(member.user_uuid, member.role, displayName(member))}
                                    >
                                        {member.role === 'owner' ? 'Demote' : 'Promote'}
                                    </button>
                                    <button
                                        type="button"
                                        class="danger-btn small"
                                        on:click={() => handleRemoveMember(member.user_uuid, displayName(member))}
                                    >
                                        Remove
                                    </button>
                                {/if}
                            </div>
                        </li>
                    {/each}

                    {#each members.filter((m) => m.status === 'invited') as member (member.uuid)}
                        <li class="member-row invited">
                            <div class="member-info">
                                <div class="member-avatar-pending" aria-hidden="true">?</div>
                                <div class="member-detail">
                                    <div class="member-name">
                                        {member.user?.email || 'Pending'}
                                        <StatusBadge label="Invited" tone="warn" />
                                    </div>
                                </div>
                            </div>
                            {#if isOwner()}
                                <div class="member-actions">
                                    <button
                                        type="button"
                                        class="danger-btn small"
                                        on:click={() => handleCancelInvite(member.uuid, member.user?.email)}
                                    >
                                        Cancel invite
                                    </button>
                                </div>
                            {/if}
                        </li>
                    {/each}
                </ul>
            </section>

            {#if isOwner()}
                <section class="section">
                    <h2 class="section-title">Invite member</h2>
                    <form class="invite-form" on:submit|preventDefault={handleInvite} novalidate>
                        <input
                            type="email"
                            inputmode="email"
                            autocomplete="off"
                            spellcheck="false"
                            bind:value={inviteEmail}
                            placeholder="colleague@company.com"
                            class="invite-input"
                            aria-label="Email address to invite"
                        />
                        <button type="submit" class="primary-btn" disabled={inviting || !inviteEmail.trim()}>
                            {#if inviting}
                                <span class="btn-spinner" aria-hidden="true"></span>
                                <span>Sending…</span>
                            {:else}
                                <span>Send invite</span>
                            {/if}
                        </button>
                    </form>
                    {#if team.max_seats && members.filter((m) => m.status === 'active').length >= team.max_seats}
                        <p class="seat-warning">All seats are filled. Upgrade your plan to add more members.</p>
                    {/if}
                </section>
            {/if}
    {/if}
</div>

<ConfirmDialog
    bind:show={confirmDialog.show}
    title={confirmDialog.title}
    message={confirmDialog.message}
    type={confirmDialog.type}
    confirmText={confirmDialog.confirmText}
    on:confirm={confirmDialog.onConfirm}
/>

<style>
    .team-page { padding: var(--space-5) var(--space-6); }
    @media (max-width: 768px) { .team-page { padding: var(--space-4); } }

    .section { margin-bottom: var(--space-8); }
    .section-title {
        font-size: var(--text-sm);
        font-weight: 600;
        letter-spacing: .04em;
        text-transform: uppercase;
        color: var(--grey-secondary);
        margin: 0 0 var(--space-4) 0;
    }

    .team-card {
        background: var(--surface);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: var(--space-5);
    }

    .team-head { display: flex; align-items: center; gap: var(--space-3); margin-bottom: var(--space-4); }
    .team-name { font-size: var(--text-xl); font-weight: 500; color: var(--grey); }

    .icon-btn {
        background: transparent;
        border: none;
        color: var(--grey-secondary);
        cursor: pointer;
        padding: var(--space-1) var(--space-2);
        border-radius: var(--radius-input);
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }
    .icon-btn :global(i.icon) { margin: 0; }
    .icon-btn:hover { color: var(--primary); background: var(--surface-hover); }
    .icon-btn:focus-visible { outline: none; box-shadow: var(--focus-ring); }

    .name-edit { display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap; }
    .name-input {
        padding: var(--space-2) var(--space-3);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-input);
        font: inherit;
        color: var(--grey);
        min-width: 220px;
    }
    .name-input:focus { outline: none; border-color: var(--primary); box-shadow: var(--focus-ring); }

    .team-meta {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-4);
        align-items: center;
        color: var(--grey-secondary);
        font-size: var(--text-sm);
    }
    .meta-item { display: inline-flex; align-items: center; gap: var(--space-2); }
    .meta-item :global(i.icon) { color: var(--primary); font-size: var(--text-base); margin: 0; }

    .leave-row { margin-top: var(--space-5); padding-top: var(--space-4); border-top: 1px solid var(--border-color); }

    .member-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: var(--space-3); }

    .member-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-4);
        padding: var(--space-4);
        background: var(--surface);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        flex-wrap: wrap;
    }
    .member-row.invited { background: var(--surface-hover); }

    .member-info { display: flex; align-items: center; gap: var(--space-3); flex: 1; min-width: 0; }

    :global(.member-info .user-avatar) { flex-shrink: 0; }

    .member-avatar-pending {
        width: 40px; height: 40px;
        border-radius: 50%;
        background: var(--grey-secondary);
        color: var(--white);
        display: flex; align-items: center; justify-content: center;
        font-weight: 600;
        flex-shrink: 0;
    }

    .member-detail { min-width: 0; flex: 1; }
    .member-name {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        flex-wrap: wrap;
        font-weight: 500;
        color: var(--grey);
    }
    .member-email { font-size: var(--text-sm); color: var(--grey-secondary); margin-top: 2px; }

    .member-actions { display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap; }

    .invite-form {
        display: flex;
        gap: var(--space-3);
        flex-wrap: wrap;
        align-items: center;
    }
    .invite-input {
        flex: 1;
        min-width: 240px;
        padding: var(--space-3);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-input);
        font: inherit;
        color: var(--grey);
    }
    .invite-input:focus { outline: none; border-color: var(--primary); box-shadow: var(--focus-ring); }

    .seat-warning {
        margin-top: var(--space-3);
        padding: var(--space-3);
        background: var(--status-warn-soft);
        color: var(--status-warn-ink);
        border-radius: var(--radius-input);
        font-size: var(--text-sm);
    }

    .primary-btn, .secondary-btn, .danger-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-2);
        padding: var(--space-3) var(--space-5);
        border-radius: var(--radius-cta);
        font: inherit;
        font-weight: 500;
        cursor: pointer;
        transition: filter .15s, background-color .15s, border-color .15s;
        border: 1px solid transparent;
    }
    .primary-btn { background: var(--primary); color: var(--white); box-shadow: var(--shadow-cta); }
    .primary-btn:hover:not(:disabled) { filter: brightness(.95); }
    .primary-btn:disabled { opacity: .7; cursor: not-allowed; }
    .primary-btn:focus-visible { outline: none; box-shadow: var(--focus-ring); }
    .primary-btn :global(i.icon) { margin: 0; }

    .secondary-btn {
        background: var(--surface);
        color: var(--grey);
        border-color: var(--border-strong);
    }
    .secondary-btn:hover { background: var(--surface-hover); border-color: var(--primary); }
    .secondary-btn:focus-visible { outline: none; box-shadow: var(--focus-ring); }

    .danger-btn {
        background: var(--surface);
        color: var(--status-danger);
        border-color: var(--status-danger);
    }
    .danger-btn:hover { background: var(--status-danger-soft); }
    .danger-btn:focus-visible { outline: none; box-shadow: var(--focus-ring); }

    .small { padding: var(--space-2) var(--space-3); font-size: var(--text-sm); }

    .btn-spinner {
        width: 1rem; height: 1rem;
        border: 2px solid rgba(255,255,255,.4);
        border-top-color: var(--white);
        border-radius: 50%;
        animation: spin .8s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }
</style>
